/**
 * /table · Restaurant detail drawer.
 *
 * The reservation routing ladder is the centerpiece. Three rungs:
 *
 *   ⚡ Direct · Instant   verified link to a known reservation platform
 *   ☎ Direct · Call      verified Places phone · concierge places the call
 *   ✉ Direct · Contact   verified website (no platform tie-up)
 *   ✦ Concierge          we secure it by hand
 *
 * The current rung (derived from backend booking_cta) is the
 * "recommended" rung; below rungs are visually dimmed.
 *
 * Anti-fabrication discipline:
 *  - Every visible field is verified (Places / resolver-emitted).
 *  - Cuisine tags come from Places `types` only.
 *  - Hours come from Places opening_hours only.
 *  - No invented blurbs / narratives. The whisper card surfaces
 *    `rank_reasons` only — already a backend-generated allowlist.
 */
import React, { useEffect, useMemo, useState } from "react";
import { canonicalVenueId } from "@/lib/venue-id";
import { BACKEND_URL } from "@/lib/backend-url";
import { RouteChip, VerifyLine } from "./route-primitives";
import {
    ROUTE,
    classifyRoute,
    classifyTiers,
    recommendedTier,
    isHardToBook,
    isGoogleReserveRoute,
    isExternalHandoff,
    verifiedRailUrl,
    RESTAURANT_LADDER,
    priceDisplay,
    cuisineLine,
    routeVia,
} from "./route-utils";
import { ShareButton } from "./ShareButton";
import { VenueContactPills } from "./VenueContactPills";
// FE fire-audit + MC courtesy ping · fires on every Reserve-direct
// click in the drawer, including Google Maps fallback venues where
// `venue.resolved_platform` is empty (so handleReserve's external-
// handoff branch would otherwise miss it · 04 Jul 2026 fix).
import { fireSelfServeIntent } from "@/lib/self-serve-intent";
import { NightlifeDrawerBlock } from "./NightlifeDrawerBlock";
import StaticMapBand from "@/components/shared/StaticMapBand";
import { DrawerVoiceLine } from "@/components/shared/DrawerAtmosphere";

const REASON_PHRASES = {
    exact_name_match: "exact name match for your query",
    venue_token_match: "matches the venue name you searched",
    venue_in_place: "in the city you searched",
    places_verified: "Concierge®-verified live",
    places_name_match: "name matches your search",
    places_destination_match: "verified restaurant in this destination",
    discovery_place_match: "in the destination you searched",
    cuisine_match: "matches the cuisine you asked for",
    price_match: "matches your price band",
    price_near: "close to your price band",
    direct_booking_available: "direct booking path available",
    direct_booking_cta: "direct booking path available",
};

function whyMatched(rankReasons) {
    const found = [];
    for (const r of rankReasons || []) {
        const head = r.split(":")[0];
        const mapped = REASON_PHRASES[head];
        if (mapped && !found.includes(mapped)) found.push(mapped);
        if (found.length >= 3) break;
    }
    return found;
}

/**
 * Tier 1 anchor texture · one labelled row (e.g. "Known For: ...").
 * The label is rendered in the green-accented mono-uppercase eyebrow
 * style consistent with the Tier-1 block; the body is the Concierge®'s
 * exact words. We pass through `.body` raw · NO interpolation, NO
 * paraphrase, NO model in the loop · what was typed in the anchor
 * record is what renders.
 */
function AnchorTextureRow({ label, body, testid }) {
    if (!body) return null;
    return (
        <div data-testid={testid} style={{ marginBottom: 10 }}>
            <div
                className="t-mono uppercase"
                style={{
                    fontSize: 9,
                    letterSpacing: "0.16em",
                    color: "var(--t-accent, #2f7a3e)",
                    fontWeight: 700,
                    marginBottom: 3,
                }}
            >
                {label}
            </div>
            <div
                style={{
                    fontSize: 13.5,
                    lineHeight: 1.55,
                    color: "var(--t-ink)",
                }}
            >
                {body}
            </div>
        </div>
    );
}

export function RestaurantDrawer({
    venue,
    onClose,
    onReserve,
    onConciergeOverride,
    onSelectNearby,
}) {
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onEsc = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onEsc);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onEsc);
        };
    }, [onClose]);

    // Rooms-nearby · the inverse cross-pillar whisper. Fires when the
    // drawer opens and the venue has coordinates. Soft-fails to empty.
    const venueId = venue?.id || venue?.place_id || "";
    const [roomsNearby, setRoomsNearby] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!venueId || !venue?.lat || !venue?.lng) return;
            try {
                const base = process.env.REACT_APP_BACKEND_URL || "";
                const r = await fetch(
                    `${base}/api/table/rooms-nearby?venue_id=${encodeURIComponent(venueId)}&limit=3`,
                );
                if (!r.ok) throw new Error("rooms_nearby_failed");
                const j = await r.json();
                if (!cancelled) setRoomsNearby(j.results || []);
            } catch {
                if (!cancelled) setRoomsNearby([]);
            }
        })();
        return () => { cancelled = true; };
    }, [venueId, venue?.lat, venue?.lng]);

    // Concierge® voice line · Parity Sprint (30 Jun 2026). One
    // atmospheric Haiku 4.5 sentence cached per venue. Falls back to
    // a category-tuned static line when LLM is unreachable.
    const [voiceLine, setVoiceLine] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!venueId || !venue?.name) return;
            try {
                const base = process.env.REACT_APP_BACKEND_URL || "";
                const params = new URLSearchParams({ name: venue.name });
                const rawSummary = venue.editorial_summary;
                const summary = (typeof rawSummary === "string") ? rawSummary
                    : (rawSummary && typeof rawSummary === "object")
                        ? (rawSummary.overview || rawSummary.text || "")
                        : "";
                if (summary) params.set("editorial", summary.slice(0, 1500));
                if (venue.neighborhood) params.set("neighborhood", venue.neighborhood);
                if (venue.city || venue.city_name) params.set("city", venue.city || venue.city_name);
                if (venue.primary_type_label || venue.cuisine) {
                    params.set("types_label", venue.primary_type_label || venue.cuisine);
                }
                const r = await fetch(
                    `${base}/api/concierge/voice/table/${encodeURIComponent(venueId)}?${params.toString()}`,
                );
                if (!r.ok) return;
                const j = await r.json();
                if (!cancelled && j?.voice_line) setVoiceLine(j.voice_line);
            } catch (_e) {
                // Silent · the drawer renders fine without a voice line.
            }
        })();
        return () => { cancelled = true; };
    }, [venueId, venue?.name, venue?.editorial_summary, venue?.neighborhood, venue?.city, venue?.city_name, venue?.primary_type_label, venue?.cuisine]);

    const routeKey = useMemo(() => classifyRoute(venue), [venue]);
    // Doctrine 8 Feb 2026 (LAB fix): the ladder is now driven by the
    // generic tier classifier · Google Reserve gets its own honest
    // tier (NOT folded into "instant" as "the venue's own link") and
    // the recommended HERO is Concierge® for hard-to-book / Michelin
    // rows. Same registry powers the nightclub card via NIGHTLIFE_LADDER.
    const tierRows = useMemo(
        () => classifyTiers(venue, RESTAURANT_LADDER),
        [venue],
    );
    const recommendedKey = useMemo(() => recommendedTier(venue), [venue]);
    if (!venue) return null;

    const route = ROUTE[routeKey];
    // Back-compat surfaces (legacy tests / sticky reserve panel below)
    const order = RESTAURANT_LADDER;
    const reach = order.indexOf(recommendedKey);
    const avail = Object.fromEntries(
        tierRows.map(({ key, available }) => [key, available]),
    );

    const tags = Array.isArray(venue.cuisine_tags) ? venue.cuisine_tags : [];
    const cuisine = cuisineLine(venue);
    const price = priceDisplay(venue.price_level);
    const hours = Array.isArray(venue.opening_hours_text) ? venue.opening_hours_text : [];
    const nearby = Array.isArray(venue.nearby_alternatives) ? venue.nearby_alternatives : [];
    const reasons = whyMatched(venue.rank_reasons);
    const via = routeVia(venue);

    return (
        <div
            data-testid="restaurant-drawer"
            className="fixed inset-0 z-40 flex justify-end"
        >
            <div
                className="t-scrim absolute inset-0"
                style={{ background: "rgba(33, 28, 21, 0.42)", backdropFilter: "blur(3px)" }}
                onClick={onClose}
                aria-label="Close drawer"
            />
            <aside
                className="t-drawer relative h-full overflow-y-auto"
                style={{
                    width: "min(540px, 100%)",
                    background: "var(--t-paper2)",
                    boxShadow: "-30px 0 80px rgba(33, 28, 21, 0.18)",
                }}
            >
                {/* sticky chrome */}
                <div
                    className="flex items-center justify-between px-7 h-16 sticky top-0 z-10"
                    style={{
                        background: "var(--t-paper2)",
                        borderBottom: "1px solid var(--t-line)",
                    }}
                >
                    <span
                        className="t-mono uppercase"
                        style={{ fontSize: 10, letterSpacing: "0.22em", color: "var(--t-ink3)" }}
                    >
                        reservation routing
                    </span>
                    <div className="flex items-center gap-3">
                        <ShareButton venue={venue} kind="drawer" />
                        {/* Founder ask 11 Feb 2026 PM late · "Save as
                           Concierge® Card" PDF · single-page branded
                           card (hero photo + brass MICHELIN eyebrow +
                           archived whisper + booking rail + brand
                           block). The backend renders it from the same
                           verified record the drawer reads · card and
                           drawer cannot diverge. Anchor element so
                           middle-click and right-click → "Open in new
                           tab" / "Save link as" both work · no JS-
                           only download trap. */}
                        {(() => {
                            const vid = canonicalVenueId(venue);
                            if (!vid) return null;
                            const apiBase = BACKEND_URL;
                            const cardUrl = `${apiBase}/api/table/venue/${encodeURIComponent(vid)}/card.pdf`;
                            return (
                                <a
                                    href={cardUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="drawer-save-as-card"
                                    aria-label={`Save Concierge® Card for ${venue.name || "venue"} as PDF`}
                                    style={{
                                        fontSize: 10,
                                        letterSpacing: "0.18em",
                                        color: "var(--t-brass)",
                                        textTransform: "uppercase",
                                        textDecoration: "none",
                                        fontFamily: "var(--t-mono, ui-monospace, monospace)",
                                        fontWeight: 600,
                                        border: "1px solid var(--t-brass)",
                                        borderRadius: 999,
                                        padding: "5px 12px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 5,
                                        cursor: "pointer",
                                        transition: "background 0.15s, color 0.15s",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = "var(--t-brass)";
                                        e.currentTarget.style.color = "var(--t-paper2)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = "transparent";
                                        e.currentTarget.style.color = "var(--t-brass)";
                                    }}
                                >
                                    <span aria-hidden="true">↓</span>
                                    save as card
                                </a>
                            );
                        })()}
                        <button
                            onClick={onClose}
                            data-testid="drawer-close"
                            aria-label="Close"
                            style={{
                                fontSize: 22,
                                color: "var(--t-ink2)",
                                background: "transparent",
                                border: 0,
                                cursor: "pointer",
                                lineHeight: 1,
                            }}
                        >
                            ×
                        </button>
                    </div>
                </div>

                {venue.image_url ? (
                    <div
                        className="relative w-full overflow-hidden"
                        style={{ height: 240, background: "var(--t-brass-wash)" }}
                    >
                        <img
                            src={venue.image_url}
                            alt={venue.name}
                            data-testid="drawer-photo"
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background:
                                    "linear-gradient(to bottom, rgba(33,28,21,0) 60%, rgba(33,28,21,0.35) 100%)",
                            }}
                        />
                    </div>
                ) : null}

                <div className="px-7 pt-7 pb-32">
                    {/* Universal Concierge® brand frame · founder
                       doctrine 7 Jun 2026 evening. Present on EVERY
                       card regardless of tier · our voice, our
                       presentation, our brand. Critically NOT
                       green-badged · the green "✓ confirmed by
                       Concierge®" badge stays reserved for anchor
                       venues a human earned. Brand frame = universal
                       presence · verification badge = earned. Keep
                       them visually different · that separation is
                       what keeps us honest.

                       This eyebrow brands the drawer top with brass
                       (the Concierge® house colour) so even on a
                       cold Places-only venue like Zuma, the first
                       impression is Les Concierges® · not "Google
                       reskin." */}
                    <div
                        data-testid="drawer-concierge-frame-eyebrow"
                        className="t-mono uppercase"
                        style={{
                            fontSize: 9.5,
                            letterSpacing: "0.22em",
                            color: "var(--t-brass)",
                            fontWeight: 700,
                            marginBottom: 14,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                        }}
                    >
                        <span style={{ fontSize: 11 }}>✦</span>
                        through your Concierge®
                        <span
                            style={{
                                flex: 1, height: 1,
                                background: "var(--t-brass)",
                                opacity: 0.35,
                                marginLeft: 4,
                            }}
                        />
                        <span
                            style={{
                                fontWeight: 400,
                                opacity: 0.7,
                                letterSpacing: "0.16em",
                            }}
                        >
                            Concierge® · est 1998
                        </span>
                    </div>

                    {/* Cuisine + price line */}
                    <span style={{ fontSize: 12, color: "var(--t-ink2)" }}>
                        {[cuisine, price].filter(Boolean).join(" · ")}
                    </span>

                    {/* Big serif venue name */}
                    <h2
                        data-testid="drawer-venue-name"
                        className="t-serif mt-1"
                        style={{
                            fontSize: 38,
                            fontWeight: 400,
                            lineHeight: 1,
                            letterSpacing: "-0.01em",
                            color: "var(--t-ink)",
                            margin: "4px 0 0",
                        }}
                    >
                        {venue.name}
                    </h2>

                    <p style={{ fontSize: 14, color: "var(--t-ink2)", marginTop: 8 }}>
                        {(() => {
                            // Item 19 polish · join the meta line
                            // honestly without stray separators when
                            // any piece is missing (the iOS ear-test
                            // glitch where city/country were empty
                            // produced a leading "· ★ 4.5").
                            const bits = [];
                            const loc = [venue.city, venue.country].filter(Boolean).join(", ");
                            if (loc) bits.push(loc);
                            if (venue.rating) {
                                bits.push(`★ ${Number(venue.rating).toFixed(1)}`);
                            }
                            return bits.join(" · ");
                        })()}
                        {venue.user_ratings_total ? (
                            <span style={{ color: "var(--t-ink3)" }}>
                                {" "}({venue.user_ratings_total.toLocaleString()} reviews)
                            </span>
                        ) : null}
                    {/* Item 10b · open / closed status · doctrine-honest
                          7 Jun 2026 evening (post-Anma round 2):

                          Asymmetric trust on `open_now` after the
                          founder showed Google says "Open" for Anma
                          while our `open_now` came back false. The
                          flag is reliable POSITIVE, unreliable
                          NEGATIVE — Places sometimes returns
                          open_now=false when the venue is in fact
                          open (timezone confusion, stale hours, the
                          Anma case). So we trust the affirmative
                          and refuse the claim on the negative.

                          Four honest states:

                          1) business_status === "CLOSED_PERMANENTLY"
                                → "● Permanently closed" (brass)
                          2) business_status === "CLOSED_TEMPORARILY"
                                → "● Temporarily closed" (brass)
                          3) open_now === true (business not closed)
                                → "● Open now" (green verified)
                          4) anything else (open_now=false / missing,
                             business_status OPERATIONAL/missing)
                                → RENDER NOTHING. The weekly hours
                                  section beneath this row carries
                                  the schedule with the doctrine
                                  Google label; the Concierge® will
                                  confirm by phone. Welcome empty
                                  space (Verified-by-Design § Gate 1
                                  + § Gate 4: never paint a closure
                                  we can't source).
                        */}
                        {(() => {
                            const bs = venue.business_status;
                            if (bs === "CLOSED_PERMANENTLY") {
                                return (
                                    <span
                                        data-testid="drawer-open-now"
                                        data-status="closed-permanently"
                                        style={{
                                            marginLeft: 10, fontSize: 11,
                                            fontWeight: 600,
                                            letterSpacing: "0.02em",
                                            color: "var(--t-brass)",
                                            background: "var(--t-card)",
                                            border: "1px solid var(--t-brass)",
                                            borderRadius: 30,
                                            padding: "2px 9px",
                                        }}
                                    >
                                        ● Permanently closed
                                    </span>
                                );
                            }
                            if (bs === "CLOSED_TEMPORARILY") {
                                return (
                                    <span
                                        data-testid="drawer-open-now"
                                        data-status="closed-temporarily"
                                        style={{
                                            marginLeft: 10, fontSize: 11,
                                            fontWeight: 600,
                                            letterSpacing: "0.02em",
                                            color: "var(--t-brass)",
                                            background: "var(--t-card)",
                                            border: "1px solid var(--t-brass)",
                                            borderRadius: 30,
                                            padding: "2px 9px",
                                        }}
                                    >
                                        ● Temporarily closed
                                    </span>
                                );
                            }
                            if (venue.open_now === true) {
                                return (
                                    <span
                                        data-testid="drawer-open-now"
                                        data-status="open-now"
                                        style={{
                                            marginLeft: 10, fontSize: 11,
                                            fontWeight: 600,
                                            letterSpacing: "0.02em",
                                            color: "var(--t-verified)",
                                            background: "var(--t-verified-wash)",
                                            border: "1px solid var(--t-verified)",
                                            borderRadius: 30,
                                            padding: "2px 9px",
                                        }}
                                    >
                                        ● Open now
                                    </span>
                                );
                            }
                            // open_now=false / missing → render nothing
                            // (let the hours block speak; doctrine
                            // forbids claiming closure we can't source)
                            return null;
                        })()}
                    </p>

                    {/* Item 10b · Photo strip · Places photos[] · keyless */}
                    {Array.isArray(venue.photos) && venue.photos.length > 1 ? (
                        <div
                            data-testid="drawer-photo-strip"
                            className="flex gap-2 mt-4 overflow-x-auto"
                            style={{
                                scrollbarWidth: "thin",
                                // Item 19 iOS polish · momentum-scroll
                                // on touch + don't trap horizontal pans.
                                WebkitOverflowScrolling: "touch",
                                scrollSnapType: "x mandatory",
                                paddingBottom: 4,
                            }}
                        >
                            {venue.photos.slice(0, 5).map((url, i) => (
                                <img
                                    key={i}
                                    src={url}
                                    alt={`${venue.name} photo ${i + 1}`}
                                    loading="lazy"
                                    data-testid={`drawer-photo-strip-${i}`}
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                    style={{
                                        flexShrink: 0,
                                        // ≥44px tap target on mobile · still
                                        // looks elegant on desktop.
                                        width: 132, height: 88,
                                        objectFit: "cover",
                                        borderRadius: 10,
                                        border: "1px solid var(--t-line2)",
                                        scrollSnapAlign: "start",
                                    }}
                                />
                            ))}
                        </div>
                    ) : null}

                    {/* SOURCED Michelin chip · slate/dim · founder doctrine
                       8 Feb 2026 ("a Michelin star is a fact, and facts
                       aren't owned; stating a true fact with attribution
                       is honest"). Renders when stars are set but NOT
                       VERIFIED 2026 — the chip is a factual citation,
                       NOT a green-badge desk-verification claim. */}
                    {(() => {
                        const at = venue.archive_texture || {};
                        const stars = at.michelin_stars;
                        const verified = at.michelin_stars_status === "VERIFIED 2026";
                        const isSourced = (
                            Number.isInteger(stars)
                            && stars >= 1
                            && stars <= 3
                            && !verified
                        );
                        if (!isSourced) return null;
                        const starsText = "★".repeat(stars);
                        const source = at.michelin_stars_source || "Michelin Guide";
                        return (
                            <div
                                data-testid="drawer-sourced-michelin-chip"
                                className="flex flex-wrap gap-2 mt-3"
                            >
                                <span
                                    className="t-mono"
                                    style={{
                                        fontSize: 10.5,
                                        letterSpacing: "0.08em",
                                        color: "var(--t-ink3)",
                                        background: "var(--t-paper2)",
                                        border: "1px solid var(--t-line2)",
                                        borderRadius: 30,
                                        padding: "4px 11px",
                                        fontWeight: 500,
                                    }}
                                    title={`Sourced from ${source} · awaiting Concierge® verification for 2026`}
                                >
                                    <span style={{
                                        color: "var(--t-ink2)",
                                        fontWeight: 600,
                                        letterSpacing: "0.06em",
                                    }}>
                                        {starsText}
                                    </span>
                                    <span style={{ marginLeft: 7 }}>
                                        per {source}
                                    </span>
                                </span>
                            </div>
                        );
                    })()}

                    {/* Item 17 Tier A · Provenance badges · NEVER fake.
                       The detector only returns a badge when the
                       venue genuinely appears on Michelin / Eater /
                       Infatuation / WoM. Each badge is a real link. */}
                    {Array.isArray(venue.provenance_badges)
                        && venue.provenance_badges.length > 0 ? (
                        <div
                            data-testid="drawer-provenance-badges"
                            className="flex flex-wrap gap-2 mt-4"
                        >
                            {venue.provenance_badges.map((b) => (
                                <a
                                    key={b.id}
                                    href={b.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`drawer-provenance-${b.id}`}
                                    className="t-mono uppercase"
                                    style={{
                                        fontSize: 10,
                                        letterSpacing: "0.16em",
                                        color: "var(--t-brass)",
                                        background: "var(--t-brass-wash)",
                                        border: "1px solid var(--t-brass2)",
                                        borderRadius: 30,
                                        padding: "5px 11px",
                                        textDecoration: "none",
                                        fontWeight: 700,
                                    }}
                                >
                                    {b.id === "michelin" ? "★ " : ""}{b.label} ↗
                                </a>
                            ))}
                        </div>
                    ) : null}

                    {/* Cuisine chips */}
                    {tags.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5 mt-4" data-testid="drawer-tags">
                            {tags.map((t) => (
                                <span
                                    key={t}
                                    className="t-mono uppercase"
                                    style={{
                                        fontSize: 10,
                                        letterSpacing: "0.14em",
                                        color: "var(--t-ink2)",
                                        border: "1px solid var(--t-line2)",
                                        background: "var(--t-paper)",
                                        borderRadius: 30,
                                        padding: "3px 9px",
                                    }}
                                >
                                    {t}
                                </span>
                            ))}
                        </div>
                    ) : null}

                    {/* Whisper · grounded "Why this matched" */}
                    {reasons.length > 0 ? (
                        <p
                            style={{
                                fontSize: 15,
                                color: "var(--t-ink)",
                                lineHeight: 1.6,
                                marginTop: 18,
                                fontStyle: "italic",
                            }}
                            data-testid="drawer-whisper"
                            className="t-serif"
                        >
                            {reasons[0].charAt(0).toUpperCase() + reasons[0].slice(1)}
                            {reasons.length > 1 ? `; ${reasons.slice(1).join("; ")}` : ""}.
                        </p>
                    ) : null}

                    {/* Concierge® voice line · Parity Sprint (30 Jun 2026).
                       One atmospheric Haiku 4.5 sentence per venue,
                       cached forever after first generation. Renders
                       below the "why this matched" whisper. */}
                    <div style={{ marginTop: 14 }}>
                      <DrawerVoiceLine
                        text={voiceLine}
                        T={{
                          bg:    "var(--t-paper)",
                          card:  "var(--t-paper2)",
                          ink:   "var(--t-ink)",
                          ink2:  "var(--t-ink2)",
                          ink3:  "var(--t-ink3)",
                          line:  "var(--t-line)",
                          line2: "var(--t-line2)",
                          brass: "var(--t-brass)",
                        }}
                        testid="drawer-concierge-voice"
                      />
                    </div>

                    {/* Description · Item 10 binding (4 Jun 2026
                       ear-test). Priority:
                         1. Google Places `editorial_summary.overview`
                         2. Venue's own homepage `og:description` /
                            `meta description` (about_text · fetched
                            by table_research.resolve_about_text)
                         3. Render nothing — NEVER compose.

                       BC-018 (founder 10 Feb 2026 · Gymkhana
                       London crash): defensively unwrap when the
                       backend leaked the raw Places dict shape
                       `{language, overview}` instead of the string.
                       Backend was hardened too, but the renderer
                       must not crash if a stale or cached card
                       lands here in the wrong shape. */}
                    {(() => {
                        const raw = venue.editorial_summary;
                        const summary = (typeof raw === "string")
                            ? raw
                            : (raw && typeof raw === "object" && typeof raw.overview === "string")
                                ? raw.overview
                                : null;
                        if (summary) {
                            return (
                                <p
                                    data-testid="drawer-editorial-summary"
                                    style={{
                                        fontSize: 14,
                                        color: "var(--t-ink2)",
                                        lineHeight: 1.6,
                                        marginTop: 12,
                                    }}
                                >
                                    {summary}
                                    <span
                                        className="t-mono uppercase"
                                        style={{
                                            display: "block",
                                            fontSize: 9,
                                            letterSpacing: "0.16em",
                                            color: "var(--t-ink3)",
                                            marginTop: 4,
                                        }}
                                    >
                                        per Concierge® · the Concierge® confirms at booking
                                    </span>
                                </p>
                            );
                        }
                        if (venue.about_text) {
                            return (
                                <p
                                    data-testid="drawer-about-text"
                                    style={{
                                        fontSize: 14,
                                        color: "var(--t-ink2)",
                                        lineHeight: 1.6,
                                        marginTop: 12,
                                    }}
                                >
                                    {venue.about_text}
                                    {venue.about_text_source ? (
                                        <a
                                            href={venue.about_text_source}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="t-mono uppercase"
                                            style={{
                                                display: "block",
                                                fontSize: 9,
                                                letterSpacing: "0.16em",
                                                color: "var(--t-ink3)",
                                                marginTop: 4,
                                                textDecoration: "none",
                                            }}
                                        >
                                            source · their site ↗
                                        </a>
                                    ) : null}
                                </p>
                            );
                        }
                        return null;
                    })()}

                    {/* G1 · single-pin Google Static Map · same band as
                       /stay, /fit and /exclusive drawers. Renders nothing
                       if the venue has no lat/lng. */}
                    <StaticMapBand
                        lat={venue.lat ?? venue?.geometry?.location?.lat}
                        lng={venue.lng ?? venue?.geometry?.location?.lng}
                        label="On the map"
                        testid="table-drawer-map"
                    />

                    {/* Tier 1 · Anchor texture · founder doctrine 7 Jun 2026.
                       The richest, most trustworthy layer · Concierge®-
                       sourced (you wrote every word). GREEN-badged
                       "confirmed by Concierge®" · this is the gold tier.
                       Renders ONLY when the venue's place_id matches an
                       anchor record · cold venues fall back cleanly to
                       Tier 3 (atmosphere) below. Same "data exists, wire
                       was missing" shape as the atmosphere fix. */}
                    {venue.anchor_texture
                        && typeof venue.anchor_texture === "object" ? (
                        <div
                            data-testid="drawer-anchor-texture"
                            style={{
                                marginTop: 18,
                                padding: "14px 16px 16px",
                                borderRadius: 14,
                                background: "var(--t-card)",
                                border: "1px solid var(--t-line2)",
                                borderLeft: "3px solid var(--t-accent, #2f7a3e)",
                            }}
                        >
                            <div
                                className="t-mono uppercase"
                                style={{
                                    fontSize: 9,
                                    letterSpacing: "0.18em",
                                    color: "var(--t-accent, #2f7a3e)",
                                    marginBottom: 10,
                                    fontWeight: 700,
                                }}
                                data-testid="drawer-anchor-eyebrow"
                            >
                                ✓ from the concierge® desk
                            </div>

                            {venue.anchor_texture.one_line ? (
                                <p
                                    data-testid="drawer-anchor-one-line"
                                    className="t-serif"
                                    style={{
                                        fontSize: 15,
                                        lineHeight: 1.5,
                                        color: "var(--t-ink)",
                                        fontStyle: "italic",
                                        marginBottom: 12,
                                    }}
                                >
                                    {venue.anchor_texture.one_line}
                                </p>
                            ) : null}

                            {venue.anchor_texture.distinction ? (
                                <AnchorTextureRow
                                    label="distinction"
                                    body={venue.anchor_texture.distinction}
                                    testid="drawer-anchor-distinction"
                                />
                            ) : null}

                            {venue.anchor_texture.must_try ? (
                                <AnchorTextureRow
                                    label="known for"
                                    body={venue.anchor_texture.must_try}
                                    testid="drawer-anchor-must-try"
                                />
                            ) : null}

                            {venue.anchor_texture.signature_menu ? (
                                <AnchorTextureRow
                                    label="signature menu"
                                    body={venue.anchor_texture.signature_menu}
                                    testid="drawer-anchor-signature-menu"
                                />
                            ) : null}

                            {venue.anchor_texture.the_room ? (
                                <AnchorTextureRow
                                    label="the room"
                                    body={venue.anchor_texture.the_room}
                                    testid="drawer-anchor-the-room"
                                />
                            ) : null}

                            {venue.anchor_texture.insider_move ? (
                                <AnchorTextureRow
                                    label="insider move"
                                    body={venue.anchor_texture.insider_move}
                                    testid="drawer-anchor-insider-move"
                                />
                            ) : null}

                            {venue.anchor_texture.dietary_note ? (
                                <AnchorTextureRow
                                    label="dietary"
                                    body={venue.anchor_texture.dietary_note}
                                    testid="drawer-anchor-dietary"
                                />
                            ) : null}

                            {venue.anchor_texture.chef ? (
                                <AnchorTextureRow
                                    label="chef"
                                    body={venue.anchor_texture.chef}
                                    testid="drawer-anchor-chef"
                                />
                            ) : null}

                            {Array.isArray(venue.anchor_texture.tags)
                                && venue.anchor_texture.tags.length > 0 ? (
                                <div
                                    data-testid="drawer-anchor-tags"
                                    className="flex flex-wrap gap-1.5"
                                    style={{ marginTop: 12 }}
                                >
                                    {venue.anchor_texture.tags.map((t, i) => (
                                        <span
                                            key={`${t}-${i}`}
                                            data-testid={`drawer-anchor-tag-${t}`}
                                            style={{
                                                fontSize: 11,
                                                color: "var(--t-ink2)",
                                                background: "var(--t-paper2, #f4f1ea)",
                                                border: "1px solid var(--t-line2)",
                                                borderRadius: 999,
                                                padding: "2px 9px",
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            ) : null}

                            <div
                                className="t-mono uppercase"
                                data-testid="drawer-anchor-source"
                                style={{
                                    fontSize: 9,
                                    letterSpacing: "0.18em",
                                    color: "var(--t-accent, #2f7a3e)",
                                    marginTop: 12,
                                    fontWeight: 600,
                                }}
                            >
                                ✓ confirmed by Concierge®
                            </div>
                        </div>
                    ) : null}

                    {/* Archive overlay (Tier 1B · lc_archive) · founder
                       direction 8 Feb 2026. Renders the GREEN "From the
                       Concierge® desk" block when a cleaned, human-
                       confirmed archive row exists for this place_id.
                       SHADOWED by `anchor_texture` (anchor is more
                       curated) · only renders when anchor_texture is
                       absent. GATED on the backend's `source` marker
                       being "confirmed by Concierge®" (which it only
                       sets when human_confirmed=True · founder doctrine
                       8 Feb 2026 · the green block is sacred to the
                       desk-confirmed row · sourced-only rows light up
                       the slate chip above, never this green block). */}
                    {!venue.anchor_texture
                        && venue.archive_texture
                        && typeof venue.archive_texture === "object"
                        && venue.archive_texture.source === "confirmed by Concierge\u00ae" ? (
                        <div
                            data-testid="drawer-archive-texture"
                            style={{
                                marginTop: 18,
                                padding: "14px 16px 16px",
                                borderRadius: 14,
                                background: "var(--t-card)",
                                border: "1px solid var(--t-line2)",
                                borderLeft: "3px solid var(--t-accent, #2f7a3e)",
                            }}
                        >
                            <div
                                className="t-mono uppercase"
                                style={{
                                    fontSize: 9,
                                    letterSpacing: "0.18em",
                                    color: "var(--t-accent, #2f7a3e)",
                                    marginBottom: 10,
                                    fontWeight: 700,
                                }}
                                data-testid="drawer-archive-eyebrow"
                            >
                                ✓ from the concierge® desk
                            </div>

                            {venue.archive_texture.concierge_whisper ? (
                                <p
                                    data-testid="drawer-archive-whisper"
                                    className="t-serif"
                                    style={{
                                        fontSize: 15,
                                        lineHeight: 1.5,
                                        color: "var(--t-ink)",
                                        fontStyle: "italic",
                                        marginBottom: 10,
                                    }}
                                >
                                    {venue.archive_texture.concierge_whisper}
                                </p>
                            ) : null}

                            {/* Whisper source attribution · founder
                               honesty ask 11 Feb 2026 PM late · close
                               the hairline-crack the audit named: the
                               grid CardInChat carries the source-line
                               under the whisper; the drawer didn't.
                               Sourced-or-silent · only renders when
                               the desk-stamped `whisper_source` (or
                               `confirmed_by`) is present alongside
                               the whisper. */}
                            {venue.archive_texture.concierge_whisper
                                && (venue.archive_texture.whisper_source
                                    || venue.archive_texture.confirmed_by) ? (
                                <p
                                    data-testid="drawer-archive-whisper-source"
                                    className="t-mono"
                                    style={{
                                        fontSize: 10,
                                        letterSpacing: "0.04em",
                                        color: "var(--t-ink3)",
                                        marginTop: -4,
                                        marginBottom: 14,
                                        fontStyle: "italic",
                                    }}
                                >
                                    &mdash;{" "}
                                    {venue.archive_texture.whisper_source
                                        || venue.archive_texture.confirmed_by}
                                </p>
                            ) : null}

                            {/* Category badge · DOCTRINE A3 fix (8 Feb 2026):
                                render ONE clean display label, not the
                                raw enum AND the subcategory side-by-side.
                                The founder caught this on LAB ("michelin"
                                + "Michelin" rendered together). */}
                            {(() => {
                                const CATEGORY_DISPLAY = {
                                    michelin: "Michelin",
                                    fine_dine: "Fine Dine",
                                    epicure: "Epicure",
                                    bar: "Bar",
                                    nightlife: "Nightlife",
                                };
                                const cat = venue.archive_texture.category_render_type;
                                const sub = venue.archive_texture.subcategory;
                                // Prefer the desk-curated subcategory string
                                // when set (e.g., "Michelin"). Else fall
                                // back to the canonical display map. Never
                                // render the raw lowercase enum.
                                const display = (
                                    (typeof sub === "string" && sub.trim())
                                    || CATEGORY_DISPLAY[cat]
                                    || (cat ? cat.replace(/_/g, " ") : null)
                                );
                                if (!display) return null;
                                return (
                                    <div
                                        data-testid={`drawer-archive-category-${cat || "unknown"}`}
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            fontSize: 11,
                                            letterSpacing: "0.08em",
                                            textTransform: "uppercase",
                                            color: "var(--t-accent, #2f7a3e)",
                                            background: "var(--t-paper2, #f4f1ea)",
                                            border: "1px solid var(--t-line2)",
                                            borderRadius: 999,
                                            padding: "3px 11px",
                                            fontWeight: 600,
                                            marginRight: 8,
                                        }}
                                    >
                                        {display}
                                    </div>
                                );
                            })()}

                            {/* Destination-batch enrichment (Aditya
                               6 Feb 2026 · St. Moritz, St. Tropez,
                               50 Best Bars, DJ Mag etc.). Known-for
                               is a short sourced tag; seasonal/season
                               surfaces only when the venue is season-
                               bounded (and the desk re-confirmed open
                               this season before badging green). */}
                            {venue.archive_texture.known_for ? (
                                <div
                                    data-testid="drawer-archive-known-for"
                                    style={{
                                        marginTop: 10,
                                        fontSize: 12,
                                        lineHeight: 1.45,
                                        color: "var(--t-ink2)",
                                    }}
                                >
                                    <span
                                        className="t-mono uppercase"
                                        style={{
                                            fontSize: 9,
                                            letterSpacing: "0.16em",
                                            color: "var(--t-accent, #2f7a3e)",
                                            marginRight: 6,
                                            fontWeight: 700,
                                        }}
                                    >
                                        known for
                                    </span>
                                    {venue.archive_texture.known_for}
                                </div>
                            ) : null}

                            {venue.archive_texture.seasonal
                                && venue.archive_texture.season ? (
                                <div
                                    data-testid="drawer-archive-season"
                                    style={{
                                        marginTop: 8,
                                        fontSize: 11,
                                        color: "var(--t-ink2)",
                                    }}
                                >
                                    <span
                                        className="t-mono uppercase"
                                        style={{
                                            fontSize: 9,
                                            letterSpacing: "0.16em",
                                            color: "var(--t-accent, #2f7a3e)",
                                            marginRight: 6,
                                            fontWeight: 700,
                                        }}
                                    >
                                        season
                                    </span>
                                    {venue.archive_texture.season}
                                </div>
                            ) : null}

                            {/* Nightlife · bar surface intentionally
                               rendered OUTSIDE this green block (see
                               below). The slate authority chip is a
                               sourced citation, not a desk-verification
                               claim — it must light up even on rows
                               that aren't yet human_confirmed (founder
                               parity ruling 9 Feb 2026). */}

                            <div
                                className="t-mono uppercase"
                                data-testid="drawer-archive-source"
                                style={{
                                    fontSize: 9,
                                    letterSpacing: "0.18em",
                                    color: "var(--t-accent, #2f7a3e)",
                                    marginTop: 12,
                                    fontWeight: 600,
                                }}
                            >
                                {/* Doctrine A2 fix (8 Feb 2026 · LAB /
                                   Konoha lesson): the green check
                                   certifies the whisper · the chip
                                   certifies the star. When both gates
                                   pass, name them BOTH so a member
                                   never wonders which one earned the
                                   green check. */}
                                {(() => {
                                    const at = venue.archive_texture || {};
                                    const starVerified = (
                                        at.michelin_stars_status === "VERIFIED 2026"
                                        && Number.isInteger(at.michelin_stars)
                                        && at.michelin_stars >= 1
                                    );
                                    if (starVerified) {
                                        return (
                                            <span
                                                data-testid="drawer-archive-source-dual-gate"
                                            >
                                                ✓ confirmed by Concierge® · ★ verified for 2026
                                            </span>
                                        );
                                    }
                                    return (
                                        <span
                                            data-testid="drawer-archive-source-prose-only"
                                        >
                                            ✓ confirmed by Concierge®
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>
                    ) : null}

                    {/* Nightlife · bar surface (founder parity ruling
                       9 Feb 2026 · LIFTED out of the green concierge
                       block above). For nightclubs & bars the slate
                       authority chip + sourced known-for + hedged
                       high-decay row are sourced citations · they
                       render whenever the overlay carries the signals,
                       regardless of human_confirmed status. The block
                       has its own slate styling and "Desk confirms
                       before you go" hedge · it is NEVER painted as
                       a verification claim. Concierge® · by hand
                       remains the route HERO via NIGHTLIFE_LADDER. */}
                    <NightlifeDrawerBlock venue={venue} />

                    {/* Atmosphere flags · founder bug 7 Jun 2026 evening.
                       The flags were paid-for ($0.175 spent at anchor-seed
                       time; 40 paid rows cached for 90 days) but the
                       drawer had no render path · they fell off entirely
                       in the verified-by-design refactor. Wired through
                       backend `/api/table/venue/{id}` → `atmosphere_chips`.
                       Doctrine label: every chip carries `per Google
                       Places · Concierge® to confirm` (Google-sourced ·
                       Google CAN be stale · Concierge® confirms before
                       the table is held). NOT green-badged · correct
                       per doctrine. */}
                    {/* Atmosphere · GROUPED render (hybrid layout ·
                       Enrichment Build Spec §1 · founder-locked 8 Feb
                       2026). Three high-value sub-eyebrows
                       (ACCESSIBILITY · DIETARY · GOOD FOR) at-glance
                       for the concierge cockpit, then a single bottom
                       "GOOD TO KNOW" strip for Drinks/Service/Parking/
                       Restroom/Payment/Price. Empty groups hidden
                       silently (asymmetric-trust rule · no labelled-
                       but-empty rows). Single doctrine footer "per
                       Google · desk confirms at booking" sits at the
                       bottom of the whole block · greys, never green
                       (Google ≠ Concierge-verified). */}
                    {(() => {
                        const chips = Array.isArray(venue.atmosphere_chips)
                            ? venue.atmosphere_chips : [];
                        if (chips.length === 0) return null;
                        // Group chips · backwards-compatible if any
                        // chip lacks `group` (legacy shape · pre-Feb-
                        // 2026 backends) it falls into goodToKnow.
                        const groups = {
                            accessibility: [],
                            dietary: [],
                            goodFor: [],
                            goodToKnow: [],
                        };
                        for (const c of chips) {
                            const g = (c && c.group) || "goodToKnow";
                            (groups[g] || groups.goodToKnow).push(c);
                        }
                        const subEyebrows = [
                            ["accessibility", "accessibility"],
                            ["dietary", "dietary"],
                            ["goodFor", "good for"],
                        ];
                        const goodToKnow = groups.goodToKnow;
                        const renderSubEyebrow = (key, label) => {
                            const items = groups[key];
                            if (!items || items.length === 0) return null;
                            return (
                                <div
                                    key={key}
                                    data-testid={`drawer-atmosphere-group-${key}`}
                                    style={{ marginBottom: 10 }}
                                >
                                    <div
                                        className="t-mono uppercase"
                                        style={{
                                            fontSize: 9,
                                            letterSpacing: "0.18em",
                                            color: "var(--t-ink3)",
                                            marginBottom: 6,
                                        }}
                                    >
                                        {label}
                                    </div>
                                    <div
                                        className="flex flex-wrap gap-1.5"
                                        data-testid={`drawer-atmosphere-chips-${key}`}
                                    >
                                        {items.map((c, i) => {
                                            // Doctrine A4 fix (8 Feb 2026):
                                            // dietary chips with value="served"
                                            // were rendering as "Dinner· served"
                                            // — awkward and ungrammatical. The
                                            // founder asked for clean phrasing.
                                            //
                                            // Rule: in the `dietary` group, when
                                            // value is "served" we render as a
                                            // single clean phrase "Serves dinner"
                                            // (mealtimes) or just the label
                                            // ("Vegetarian options" — implicit
                                            // serves). Other groups keep the
                                            // label · value format for chips
                                            // with non-"yes" values.
                                            const MEALS = new Set([
                                                "Breakfast", "Brunch", "Lunch",
                                                "Dinner", "Dessert", "Coffee",
                                            ]);
                                            const isDietaryServed = (
                                                key === "dietary"
                                                && c.value === "served"
                                            );
                                            let chipBody;
                                            if (isDietaryServed && MEALS.has(c.label)) {
                                                chipBody = (
                                                    <span style={{ fontWeight: 600 }}>
                                                        Serves {c.label.toLowerCase()}
                                                    </span>
                                                );
                                            } else if (isDietaryServed) {
                                                // "Vegetarian options · served"
                                                // → drop the value qualifier ·
                                                // label alone reads clean.
                                                chipBody = (
                                                    <span style={{ fontWeight: 600 }}>
                                                        {c.label}
                                                    </span>
                                                );
                                            } else {
                                                chipBody = (
                                                    <>
                                                        <span style={{ fontWeight: 600 }}>
                                                            {c.label}
                                                        </span>
                                                        {c.value && c.value !== "yes" ? (
                                                            <span
                                                                style={{
                                                                    color: "var(--t-ink2)",
                                                                    marginLeft: 6,
                                                                }}
                                                            >
                                                                · {c.value}
                                                            </span>
                                                        ) : null}
                                                    </>
                                                );
                                            }
                                            return (
                                                <span
                                                    key={`${key}-${c.label}-${i}`}
                                                    data-testid={`drawer-atmosphere-chip-${c.label.replace(/\s+/g, "-")}`}
                                                    title={c.source}
                                                    style={{
                                                        fontSize: 12.5,
                                                        color: "var(--t-ink)",
                                                        background: "var(--t-card)",
                                                        border: "1px solid var(--t-line2)",
                                                        borderRadius: 14,
                                                        padding: "4px 11px",
                                                        lineHeight: 1.3,
                                                    }}
                                                >
                                                    {chipBody}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        };
                        const hasGoodToKnow = goodToKnow.length > 0;
                        const hasSubEyebrows = subEyebrows.some(
                            ([k]) => (groups[k] || []).length > 0,
                        );
                        if (!hasSubEyebrows && !hasGoodToKnow) return null;
                        return (
                            <div
                                data-testid="drawer-atmosphere"
                                className="mt-4"
                                style={{
                                    paddingTop: 12,
                                    borderTop: "1px solid var(--t-line2)",
                                }}
                            >
                                {subEyebrows.map(([k, lbl]) =>
                                    renderSubEyebrow(k, lbl))}
                                {hasGoodToKnow ? (
                                    <div
                                        data-testid="drawer-atmosphere-group-goodToKnow"
                                        style={{ marginBottom: 6 }}
                                    >
                                        <div
                                            className="t-mono uppercase"
                                            style={{
                                                fontSize: 9,
                                                letterSpacing: "0.18em",
                                                color: "var(--t-ink3)",
                                                marginBottom: 6,
                                            }}
                                        >
                                            good to know
                                        </div>
                                        <div
                                            className="flex flex-wrap gap-1.5"
                                            data-testid="drawer-atmosphere-chips-goodToKnow"
                                        >
                                            {goodToKnow.map((c, i) => (
                                                <span
                                                    key={`gtk-${c.label}-${i}`}
                                                    data-testid={`drawer-atmosphere-chip-${c.label.replace(/\s+/g, "-")}`}
                                                    title={c.source}
                                                    style={{
                                                        fontSize: 12.5,
                                                        color: "var(--t-ink)",
                                                        background: "var(--t-card)",
                                                        border: "1px solid var(--t-line2)",
                                                        borderRadius: 14,
                                                        padding: "4px 11px",
                                                        lineHeight: 1.3,
                                                    }}
                                                >
                                                    <span style={{ fontWeight: 600 }}>
                                                        {c.label}
                                                    </span>
                                                    {c.value && c.value !== "yes" ? (
                                                        <span
                                                            style={{
                                                                color: "var(--t-ink2)",
                                                                marginLeft: 6,
                                                            }}
                                                        >
                                                            · {c.value}
                                                        </span>
                                                    ) : null}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}
                                <div
                                    className="t-mono uppercase"
                                    data-testid="drawer-atmosphere-source"
                                    style={{
                                        fontSize: 9,
                                        letterSpacing: "0.16em",
                                        color: "var(--t-ink3)",
                                        marginTop: 6,
                                    }}
                                >
                                    per Google · the Concierge® confirms at booking
                                </div>
                            </div>
                        );
                    })()}

                    {/* Menu · Item 10 "extract not generate" (4 Jun 2026
                       ear-test). Priority:
                         1. While detail is loading: "Reading their menu…"
                         2. menu_items + source link (verified-extracted)
                         3. Just the "See full menu →" link
                         4. Render nothing (no link, no items)
                       The drawer NEVER renders a composed dish list. */}
                    {(() => {
                        const mlink = venue.menu_link;
                        const menuUrl = (mlink && typeof mlink === "object")
                            ? mlink.url
                            : (typeof mlink === "string" ? mlink : null);
                        const menuLabel = (mlink && typeof mlink === "object")
                            ? (mlink.label || "View menu")
                            : "View menu";

                        // 1) Research in flight · honest placeholder
                        if (venue._detail_loading
                            && !venue.menu_items
                            && (venue.website || menuUrl)) {
                            return (
                                <p
                                    data-testid="drawer-menu-loading"
                                    className="t-mono uppercase"
                                    style={{
                                        marginTop: 14,
                                        fontSize: 10,
                                        letterSpacing: "0.18em",
                                        color: "var(--t-ink3)",
                                    }}
                                >
                                    · reading their menu…
                                </p>
                            );
                        }

                        // 2) Extracted dishes · with source link
                        if (Array.isArray(venue.menu_items)
                            && venue.menu_items.length > 0) {
                            const src = venue.menu_items[0]?.source_url
                                || menuUrl;
                            return (
                                <div
                                    className="mt-4"
                                    data-testid="drawer-menu-items"
                                >
                                    <div
                                        className="t-mono uppercase"
                                        style={{
                                            fontSize: 9,
                                            letterSpacing: "0.18em",
                                            color: "var(--t-ink3)",
                                            marginBottom: 6,
                                        }}
                                    >
                                        menu highlights · extracted from their menu
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {venue.menu_items.slice(0, 6).map((it, i) => (
                                            <span
                                                key={i}
                                                data-testid={`drawer-menu-item-${i}`}
                                                style={{
                                                    fontSize: 12.5,
                                                    color: "var(--t-ink)",
                                                    background: "var(--t-card)",
                                                    border: "1px solid var(--t-line2)",
                                                    borderRadius: 14,
                                                    padding: "4px 11px",
                                                }}
                                            >
                                                {it.name}
                                            </span>
                                        ))}
                                    </div>
                                    {src ? (
                                        <a
                                            href={src}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="drawer-menu-items-source"
                                            className="t-mono uppercase"
                                            style={{
                                                display: "inline-block",
                                                marginTop: 8,
                                                fontSize: 9,
                                                letterSpacing: "0.16em",
                                                color: "var(--t-ink3)",
                                                textDecoration: "none",
                                            }}
                                        >
                                            source · {(() => {
                                                try {
                                                    return new URL(src).hostname.replace(/^www\./, "");
                                                } catch { return "their site"; }
                                            })()} ↗
                                        </a>
                                    ) : null}
                                </div>
                            );
                        }

                        // 3) Just the link · NEVER a generated list
                        if (menuUrl) {
                            return (
                                <a
                                    href={menuUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="drawer-menu-link"
                                    className="t-mono uppercase"
                                    style={{
                                        display: "inline-block",
                                        marginTop: 12,
                                        fontSize: 11,
                                        letterSpacing: "0.14em",
                                        color: "var(--t-brass)",
                                        textDecoration: "none",
                                        fontWeight: 600,
                                        border: "1px solid var(--t-brass2)",
                                        borderRadius: 30,
                                        padding: "5px 12px",
                                    }}
                                >
                                    See full menu ↗
                                </a>
                            );
                        }

                        // 4) Render nothing · honest fall-through
                        return null;
                    })()}

                    {/* Verification ledger panel removed 30 Jun 2026 evening
                       (founder ruling: "remove the first pic words on table
                       and put Reserve concierge line first like we have
                       with others"). The drawer now opens straight from the
                       Concierge® voice line into the routing ladder · same
                       clean rhythm as /stay /fit /airport /exclusive. */}

                    {/* The routing ladder */}
                    <p
                        className="t-mono uppercase mt-8 mb-3"
                        style={{
                            fontSize: 10,
                            letterSpacing: "0.18em",
                            color: "var(--t-ink3)",
                        }}
                    >
                        how we'll get you in
                    </p>
                    <div className="relative" data-testid="routing-ladder">
                        <div
                            style={{
                                position: "absolute",
                                left: 19,
                                top: 14,
                                bottom: 14,
                                width: 2,
                                background: "var(--t-line2)",
                            }}
                        />
                        {order.map((k, idx) => {
                            const ro = ROUTE[k];
                            const on = avail[k];
                            // Doctrine 8 Feb 2026: "recommended" badge
                            // tracks the recommended HERO key (Concierge®
                            // for hard-to-book / Michelin), NOT the
                            // classified route. Tiers below the hero
                            // are honest fallbacks, not the hero.
                            const best = k === recommendedKey;
                            return (
                                <div
                                    key={k}
                                    className="relative flex gap-4 pb-3"
                                    data-testid={`ladder-rung-${k}`}
                                >
                                    <span
                                        className="relative z-10 flex items-center justify-center shrink-0"
                                        style={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: 30,
                                            background: on ? ro.wash : "var(--t-paper)",
                                            border: `1.5px solid ${on ? ro.color : "var(--t-line2)"}`,
                                            color: on ? ro.color : "var(--t-ink3)",
                                            fontSize: 15,
                                        }}
                                        aria-hidden="true"
                                    >
                                        {ro.icon}
                                    </span>
                                    <div
                                        className="flex-1 pb-1"
                                        style={{ opacity: on ? 1 : 0.45 }}
                                    >
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    color: on ? ro.color : "var(--t-ink3)",
                                                }}
                                            >
                                                {ro.label}
                                                {via && best && k !== "concierge" && k !== "google_reserve" ? (
                                                    <span
                                                        style={{
                                                            color: "var(--t-ink3)",
                                                            fontWeight: 400,
                                                            marginLeft: 6,
                                                        }}
                                                    >
                                                        · via {via}
                                                    </span>
                                                ) : null}
                                            </span>
                                            {best ? (
                                                <span
                                                    className="t-mono uppercase"
                                                    data-testid={`ladder-recommended-${k}`}
                                                    style={{
                                                        fontSize: 8.5,
                                                        letterSpacing: "0.12em",
                                                        background: "var(--t-ink)",
                                                        color: "var(--t-paper2)",
                                                        borderRadius: 20,
                                                        padding: "2px 7px",
                                                    }}
                                                >
                                                    recommended
                                                </span>
                                            ) : null}
                                        </div>
                                        <p
                                            style={{
                                                fontSize: 12.5,
                                                color: "var(--t-ink2)",
                                                marginTop: 2,
                                            }}
                                        >
                                            {on ? ro.line : "Not available for this venue."}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Facts grid (only render rows we actually have) */}
                    {(hours.length > 0 || venue.address || tags.length > 0) ? (
                        <div
                            className="mt-7 grid grid-cols-1 gap-px overflow-hidden"
                            style={{
                                background: "var(--t-line)",
                                border: "1px solid var(--t-line)",
                                borderRadius: 13,
                            }}
                            data-testid="facts-grid"
                        >
                            {/* Item 20a fix · the duplicate "Hours" block
                                that USED to render here has been removed.
                                The canonical hours render lives below as
                                `[data-testid=drawer-hours-block]` (Item
                                10b · verified Places weekday_text). */}
                            {venue.address ? (
                                <div
                                    className="flex gap-4"
                                    style={{ background: "var(--t-card)", padding: "12px 15px" }}
                                >
                                    <span
                                        className="t-mono uppercase"
                                        style={{
                                            fontSize: 9.5,
                                            letterSpacing: "0.1em",
                                            color: "var(--t-ink3)",
                                            width: 80,
                                            flexShrink: 0,
                                            paddingTop: 2,
                                        }}
                                    >
                                        Address
                                    </span>
                                    <div className="flex-1">
                                        <div
                                            style={{ fontSize: 13.5, color: "var(--t-ink)" }}
                                            data-testid="drawer-address"
                                        >
                                            {venue.address}
                                        </div>
                                        {venue.map_url ? (
                                            <a
                                                href={venue.map_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid="drawer-map-link"
                                                className="t-mono uppercase"
                                                style={{
                                                    display: "inline-block",
                                                    marginTop: 6,
                                                    fontSize: 10,
                                                    letterSpacing: "0.14em",
                                                    color: "var(--t-brass)",
                                                    textDecoration: "none",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                View on map ↗
                                            </a>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                            {/* Item 10b · Hours block · verified Places weekday_text */}
                            {hours.length > 0 ? (
                                <div
                                    className="flex gap-4"
                                    style={{ background: "var(--t-card)", padding: "12px 15px" }}
                                    data-testid="drawer-hours-block"
                                >
                                    <span
                                        className="t-mono uppercase"
                                        style={{
                                            fontSize: 9.5,
                                            letterSpacing: "0.1em",
                                            color: "var(--t-ink3)",
                                            width: 80,
                                            flexShrink: 0,
                                            paddingTop: 2,
                                        }}
                                    >
                                        Hours
                                    </span>
                                    <div
                                        className="flex-1 t-mono"
                                        style={{
                                            fontSize: 12.5,
                                            color: "var(--t-ink2)",
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {hours.map((line, i) => (
                                            <div key={i}>{line}</div>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                            {tags.length > 0 ? (
                                <div
                                    className="flex gap-4"
                                    style={{ background: "var(--t-card)", padding: "12px 15px" }}
                                >
                                    <span
                                        className="t-mono uppercase"
                                        style={{
                                            fontSize: 9.5,
                                            letterSpacing: "0.1em",
                                            color: "var(--t-ink3)",
                                            width: 80,
                                            flexShrink: 0,
                                            paddingTop: 2,
                                        }}
                                    >
                                        Known for
                                    </span>
                                    <span style={{ fontSize: 13.5, color: "var(--t-ink)" }}>
                                        {tags.join(" · ")}
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {/* Nearby · also verified */}
                    {nearby.length > 0 ? (
                        <div className="mt-7" data-testid="drawer-nearby">
                            <p
                                className="t-mono uppercase mb-3"
                                style={{
                                    fontSize: 10,
                                    letterSpacing: "0.18em",
                                    color: "var(--t-ink3)",
                                }}
                            >
                                similar · also verified
                            </p>
                            <div className="space-y-2">
                                {nearby.map((n) => (
                                    <button
                                        type="button"
                                        key={n.id}
                                        data-testid={`drawer-nearby-${n.id}`}
                                        onClick={() => onSelectNearby?.(n)}
                                        className="w-full flex items-start justify-between gap-3 py-2 text-left transition-colors"
                                        style={{
                                            borderBottom: "1px solid var(--t-line)",
                                            background: "transparent",
                                            border: 0,
                                            borderBottomWidth: 1,
                                            borderBottomStyle: "solid",
                                            borderBottomColor: "var(--t-line)",
                                            cursor: "pointer",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = "var(--t-card)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = "transparent";
                                        }}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-baseline gap-2 flex-wrap">
                                                <span
                                                    className="t-serif"
                                                    style={{
                                                        fontSize: 15,
                                                        color: "var(--t-ink)",
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {n.name}
                                                </span>
                                                {n.cuisine_match ? (
                                                    <span
                                                        className="t-mono uppercase"
                                                        data-testid={`nearby-cuisine-badge-${n.id}`}
                                                        style={{
                                                            fontSize: 8.5,
                                                            letterSpacing: "0.14em",
                                                            color: "var(--t-verified)",
                                                            background: "var(--t-verified-wash)",
                                                            border: "1px solid var(--t-verified)",
                                                            borderRadius: 20,
                                                            padding: "1px 6px",
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        similar
                                                    </span>
                                                ) : null}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 11.5,
                                                    color: "var(--t-ink3)",
                                                    marginTop: 2,
                                                }}
                                            >
                                                {n.address || ""}
                                            </div>
                                            {(n.cuisine_tags || []).length > 0 ? (
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {n.cuisine_tags.slice(0, 3).map((t) => (
                                                        <span
                                                            key={t}
                                                            style={{
                                                                fontSize: 9.5,
                                                                color: "var(--t-ink3)",
                                                            }}
                                                        >
                                                            {t}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : null}
                                        </div>
                                        <div className="text-right shrink-0">
                                            {n.rating ? (
                                                <div
                                                    style={{ fontSize: 12, color: "var(--t-ink2)" }}
                                                >
                                                    ★ {Number(n.rating).toFixed(1)}
                                                    {n.user_ratings_total ? (
                                                        <span
                                                            className="t-mono"
                                                            style={{
                                                                fontSize: 9.5,
                                                                color: "var(--t-ink3)",
                                                                marginLeft: 4,
                                                            }}
                                                        >
                                                            ({n.user_ratings_total >= 1000 ? `${(n.user_ratings_total / 1000).toFixed(n.user_ratings_total >= 10000 ? 0 : 1)}k` : n.user_ratings_total})
                                                        </span>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                            {n.distance_km !== null && n.distance_km !== undefined ? (
                                                <div
                                                    className="t-mono"
                                                    style={{
                                                        fontSize: 10,
                                                        color: "var(--t-ink3)",
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    {n.distance_km < 1
                                                        ? `${Math.round(n.distance_km * 1000)} m`
                                                        : `${n.distance_km.toFixed(1)} km`}
                                                </div>
                                            ) : null}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {/* Item 17 Tier B · What people say (link-out only) */}
                    {Array.isArray(venue.what_people_say)
                        && venue.what_people_say.length > 0 ? (
                        <div className="mt-7" data-testid="drawer-what-people-say">
                            <div
                                className="t-mono uppercase"
                                style={{
                                    fontSize: 9.5,
                                    letterSpacing: "0.18em",
                                    color: "var(--t-ink3)",
                                    marginBottom: 8,
                                }}
                            >
                                what people say · link-out only
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {venue.what_people_say.map((link) => (
                                    <a
                                        key={link.id}
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`drawer-wps-${link.id}`}
                                        style={{
                                            fontSize: 12,
                                            color: "var(--t-ink)",
                                            background: "var(--t-card)",
                                            border: "1px solid var(--t-line2)",
                                            borderRadius: 14,
                                            padding: "4px 11px",
                                            textDecoration: "none",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {link.label} ↗
                                    </a>
                                ))}
                            </div>
                            <p
                                className="t-mono"
                                style={{
                                    fontSize: 10,
                                    color: "var(--t-ink3)",
                                    marginTop: 8,
                                    fontStyle: "italic",
                                }}
                            >
                                we never reproduce or summarise their content · we just route you.
                            </p>
                        </div>
                    ) : null}

                    {/* Contact channels · founder ear-test 6 Jun 2026
                        evening. WhatsApp / Call / Email / Site pills
                        for THIS venue · pre-filled with a reservation
                        enquiry. Only channels the venue has actually
                        published · no invented contacts. */}
                    {(venue.phone_active || venue.whatsapp_active
                        || venue.email_active || venue.website_active) ? (
                        <div className="px-7 py-5 border-t"
                             style={{ borderColor: "var(--t-line)" }}>
                            <div
                                className="t-mono uppercase"
                                style={{
                                    fontSize: 10,
                                    letterSpacing: "0.22em",
                                    color: "var(--t-ink3)",
                                    marginBottom: 8,
                                }}
                            >
                                reach the venue \u00b7 their own channels
                            </div>
                            <VenueContactPills
                                venue={venue}
                                testIdPrefix="drawer-contact"
                            />
                            <p
                                className="t-mono"
                                style={{
                                    fontSize: 10,
                                    color: "var(--t-ink3)",
                                    marginTop: 8,
                                    fontStyle: "italic",
                                }}
                            >
                                only contacts the venue has actually published.
                                we never invent.
                            </p>
                        </div>
                    ) : null}

                    {/* Universal Concierge® voice footer · founder
                       doctrine 7 Jun 2026 evening. Present on EVERY
                       drawer regardless of tier · our voice closing
                       the experience. The doctrine spoken plainly:
                       we trace every fact, we route to a human when
                       it matters. Brass-toned, NOT green-badged ·
                       keeps the verification badge sacred for the
                       anchor block above. */}
                    <div
                        data-testid="drawer-concierge-voice-footer"
                        style={{
                            marginTop: 22,
                            padding: "16px 18px",
                            borderTop: "1px solid var(--t-line2)",
                            borderRadius: 0,
                            background: "var(--t-paper2, #f4f1ea)",
                            borderLeft: "2px solid var(--t-brass)",
                        }}
                    >
                        <div
                            className="t-mono uppercase"
                            style={{
                                fontSize: 9,
                                letterSpacing: "0.22em",
                                color: "var(--t-brass)",
                                fontWeight: 700,
                                marginBottom: 6,
                            }}
                        >
                            ✦ how we work
                        </div>
                        <p
                            className="t-serif"
                            style={{
                                fontSize: 13.5,
                                lineHeight: 1.55,
                                color: "var(--t-ink2)",
                                margin: 0,
                                fontStyle: "italic",
                            }}
                        >
                            We never display a contact we haven't traced to
                            its source. When you reserve, a human Concierge®
                            routes you in.
                        </p>
                    </div>
                </div>

                {/* Reserve panel · reflects the recommended HERO tier.
                    Doctrine 8 Feb 2026 (LAB fix): for hard-to-book/Michelin
                    rows the recommended hero is Concierge®, so the primary
                    CTA reflects THAT — not whatever Google Reserve/direct
                    link the venue happens to expose.
                    Founder ruling 30 Jun 2026: removed `sticky bottom-0`
                    — the pinned-to-bottom behaviour was causing content
                    below (Concierge® Noticed nearby hotels) to scroll under
                    the panel mid-drawer. The panel now renders inline at
                    the end of the booking-tier section, which keeps the
                    reading order honest and doesn't trap content beneath. */}
                <div
                    className="px-7 py-4"
                    style={{
                        background: "var(--t-paper2)",
                        borderTop: "1px solid var(--t-line)",
                    }}
                >
                    {(() => {
                        // BC-023 (founder ruling 10 Feb 2026 PM): the
                        // CTA stack is ALWAYS:
                        //   PRIMARY  = verified booking rail (Google
                        //              Reserve / TheFork / Resy / Nomad /
                        //              Chope / Swiggy Dineout / etc.)
                        //   SECONDARY = "or have Concierge® reserve it
                        //               for you →" · brass · prominent ·
                        //               always present so the member has
                        //               a real choice.
                        // The previous design buried Concierge® as a tiny
                        // text link for non-Michelin and inverted them
                        // for Michelin (Concierge primary, rail tiny).
                        // Founder ruled that BOTH must be brass-quality
                        // surfaces · "the customer has a choice".
                        //
                        // The only time the layout collapses to a SINGLE
                        // brass Concierge® primary (no secondary) is when
                        // the venue has no verified self-service rail at
                        // all · Concierge® is then the only honest path.
                        const railHref = verifiedRailUrl(venue);
                        const railName = (
                            venue?.resolved_platform?.platform
                            || venue?.route_via
                            || venue?.booking_platform
                            || null
                        );
                        const railRouteKey = railHref ? routeKey : "concierge";
                        const isRailExternal = (
                            railHref && isExternalHandoff(railName, railHref)
                        );
                        // K1.2 (14 Jun 2026 · founder ruling 30 Jun 2026):
                        // Primary CTA label is now simply "Reserve" — the
                        // brand promise is Concierge®-first, and exposing
                        // raw supplier names like "Continue on Google /
                        // SevenRooms" would dilute that. The actual URL /
                        // handoff routing is preserved; this is purely a
                        // member-facing label. The named rail is still
                        // available as `data-rail-name` for telemetry.
                        const railLabel = "Reserve";
                        const railNameForData = (() => {
                            const n = (railName || "").trim();
                            if (!n) return null;
                            const norm = n.toLowerCase()
                                .replace(/_/g, " ").trim();
                            return {
                                "google reserve": "google_reserve",
                                "google": "google_reserve",
                                "thefork": "thefork",
                                "the fork": "thefork",
                                "lafourchette": "thefork",
                                "resy": "resy",
                                "opentable": "opentable",
                                "sevenrooms": "sevenrooms",
                                "tablecheck": "tablecheck",
                                "tock": "tock",
                                "tabelog": "tabelog",
                                "chope": "chope",
                                "quandoo": "quandoo",
                                "bookatable": "bookatable",
                                "eazydiner": "eazydiner",
                                "dineout": "dineout",
                                "swiggy dineout": "swiggy_dineout",
                                "zomato": "zomato",
                            }[norm] || norm.replace(/\s+/g, "_");
                        })();
                        const primaryBg = "var(--t-ink)";
                        const primaryStyle = {
                            background: primaryBg,
                            color: "var(--t-paper2)",
                            borderRadius: 14,
                            padding: "16px",
                            fontSize: 15,
                            fontWeight: 600,
                            border: 0,
                            cursor: "pointer",
                            letterSpacing: "0.01em",
                            textDecoration: "none",
                        };
                        // Concierge® secondary is now a REAL brass-toned
                        // button (not the tiny text-link from the
                        // pre-BC-023 design). Member must see it as a
                        // first-class alternative · "the customer has a
                        // choice".
                        const secondaryStyle = {
                            background: "transparent",
                            color: "var(--t-brass)",
                            borderRadius: 14,
                            padding: "13px",
                            fontSize: 13.5,
                            fontWeight: 600,
                            border: "1px solid var(--t-brass)",
                            cursor: "pointer",
                            letterSpacing: "0.01em",
                            textDecoration: "none",
                            marginTop: 10,
                            display: "block",
                            width: "100%",
                            textAlign: "center",
                        };
                        return (
                            <>
                                {/* Founder ruling 30 Jun 2026 evening:
                                   Request via Concierge® ALWAYS comes
                                   first as the primary (filled black)
                                   CTA. Reserve becomes the quiet
                                   secondary outline. Same order across
                                   every pillar. */}
                                <button
                                    data-testid="drawer-primary-cta"
                                    onClick={() => {
                                        if (onConciergeOverride) onConciergeOverride(venue);
                                        else onReserve?.(venue, "concierge");
                                    }}
                                    className="w-full flex items-center justify-center gap-2"
                                    style={primaryStyle}
                                >
                                    Request via Concierge<span style={{ fontSize: 11 }}>{"\u00ae"}</span>
                                </button>
                                {railHref ? (
                                    isRailExternal ? (
                                        <a
                                            data-testid="drawer-reserve-secondary"
                                            data-rail-name={railNameForData || undefined}
                                            href={railHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => {
                                                // Fire the FE audit + MC courtesy ping
                                                // BEFORE the anchor navigates. Runs even
                                                // for Google-Maps-fallback venues where
                                                // `resolved_platform` isn't populated (the
                                                // gap that hid all real member clicks on
                                                // Chefs à Table etc · 04 Jul 2026).
                                                try {
                                                    fireSelfServeIntent({
                                                        benefit: "table",
                                                        venue,
                                                        extra: {
                                                            booking_platform: railName || undefined,
                                                            destination_url:  railHref,
                                                        },
                                                    });
                                                } catch { /* swallow · audit is courtesy */ }
                                                onReserve?.(venue, railRouteKey);
                                            }}
                                            className="w-full"
                                            style={secondaryStyle}
                                        >
                                            {railLabel} <span style={{ fontSize: 13 }}>{"\u2197"}</span>
                                        </a>
                                    ) : (
                                        <button
                                            data-testid="drawer-reserve-secondary"
                                            data-rail-name={railNameForData || undefined}
                                            onClick={() => {
                                                try {
                                                    fireSelfServeIntent({
                                                        benefit: "table",
                                                        venue,
                                                        extra: {
                                                            booking_platform: railName || undefined,
                                                            destination_url:  railHref,
                                                        },
                                                    });
                                                } catch { /* swallow */ }
                                                onReserve?.(venue, railRouteKey);
                                            }}
                                            className="w-full"
                                            style={secondaryStyle}
                                        >
                                            {railLabel} <span style={{ fontSize: 13 }}>{"\u2197"}</span>
                                        </button>
                                    )
                                ) : null}
                            </>
                        );
                    })()}

                    {/* Rooms nearby · the quiet inverse of /stay's
                       "Tables within reach". Renders only when we
                       have 1-3 real hotels within walking distance.
                       Soft cross-pillar whisper · ignorable. */}
                    {roomsNearby && roomsNearby.length > 0 && (
                        <div
                            data-testid="drawer-rooms-nearby"
                            style={{
                                marginTop: 24,
                                padding: "16px 18px",
                                background: "var(--t-brass-wash, rgba(160,122,56,0.06))",
                                border: "1px solid var(--t-brass2, rgba(160,122,56,0.22))",
                                borderRadius: 14,
                            }}
                        >
                            <p
                                className="t-serif"
                                style={{
                                    fontSize: 14,
                                    fontStyle: "italic",
                                    color: "var(--t-ink, #2b231a)",
                                    margin: "0 0 12px",
                                    lineHeight: 1.5,
                                }}
                            >
                                <span
                                    className="t-mono uppercase"
                                    style={{
                                        fontSize: 10,
                                        letterSpacing: "0.2em",
                                        color: "var(--t-brass, #8b6914)",
                                        fontStyle: "normal",
                                        marginRight: 8,
                                    }}
                                >
                                    Concierge® noticed
                                </span>
                                No room booked yet? Three options walking distance.
                            </p>
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                {roomsNearby.map((h) => (
                                    <a
                                        key={h.place_id}
                                        data-testid={`drawer-room-chip-${h.place_id}`}
                                        href={`/stay?q=${encodeURIComponent(h.name)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            display: "flex",
                                            alignItems: "baseline",
                                            justifyContent: "space-between",
                                            gap: 10,
                                            padding: "10px 14px",
                                            background: "var(--t-card, #fff)",
                                            border: "1px solid var(--t-line2, rgba(43,35,26,0.1))",
                                            borderRadius: 12,
                                            textDecoration: "none",
                                            color: "var(--t-ink, #2b231a)",
                                            transition: "all 120ms ease",
                                        }}
                                    >
                                        <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
                                            <span style={{
                                                fontSize: 14,
                                                fontWeight: 500,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}>{h.name}</span>
                                            <span style={{
                                                fontSize: 11.5,
                                                color: "var(--t-ink3, rgba(43,35,26,0.55))",
                                            }}>
                                                ★ {h.rating}
                                                {h.user_ratings_total ? ` · ${h.user_ratings_total.toLocaleString()} ratings` : ""}
                                            </span>
                                        </span>
                                        <span
                                            className="t-mono"
                                            style={{
                                                fontSize: 11,
                                                color: "var(--t-ink2, rgba(43,35,26,0.75))",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {h.walk_min != null ? `${h.walk_min} min walk` : ""}
                                        </span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </aside>
        </div>
    );
}
